// =============================================================================
// File: frontend/src/components/PaidGate.jsx
// Reusable gate. Two modes:
//   1. Subscription lockout (no requiredTier) — blocks when the user is not an
//      active/trialing/past_due subscriber (e.g. trial expired → read-only).
//   2. Tier feature gate (requiredTier="pro"|"max") — blocks when the user's
//      effective tier is below the requirement (e.g. Digital Twin needs Pro).
// Trialing users get full access (trial = Max-level credits + full Digital Twin).
// CTA routes to the Account tab, which hosts all tier details + checkout.
// Patent claims exercised: P-007 Claims 1-6
// =============================================================================

import React from 'react';

const TIER_RANK = { base: 1, pro: 2, trial: 3, max: 3 };  // trial = full feature access
const TIER_NAMES = { base: 'Healist Base', pro: 'Healist Pro', max: 'Healist Max' };

function PaidGate({ user, featureName, requiredTier, children, onUpgradeClick }) {
  // Single source — backend-resolved user.is_subscriber (#consistency P1/C). No FE recompute.
  const isSubscriber = () => !!user?.is_subscriber;

  // Single source — backend-resolved effective_tier (#consistency P0/A). No FE recompute.
  const effectiveRank = () => TIER_RANK[user?.effective_tier] || 0;

  const hasAccess = () => {
    if (!user) return false;
    if (user.permanent_test_acct) return true;
    if (!isSubscriber()) return false;                          // locked out (trial expired / canceled)
    if (!requiredTier) return true;                             // subscription gate only
    return effectiveRank() >= (TIER_RANK[requiredTier] || 0);   // tier feature gate
  };

  if (hasAccess()) {
    return <>{children}</>;
  }

  const goToPlans = () => {
    if (typeof onUpgradeClick === 'function') onUpgradeClick();
    else window.location.href = '/?tab=account';
  };

  const lockedOut = !isSubscriber() && !user?.permanent_test_acct;
  const tierName = requiredTier ? TIER_NAMES[requiredTier] : 'a paid plan';
  const feature = featureName || 'This feature';

  const title = lockedOut ? 'Your access is paused' : `${feature} requires ${tierName}`;
  const description = lockedOut
    ? 'Your free trial has ended. Your data is safe — choose a plan to unlock AI features and actions again.'
    : `${feature} is included with ${tierName} and above. Upgrade to unlock it.`;
  const cta = lockedOut ? 'Choose a Plan' : `Upgrade to ${tierName}`;

  return (
    <div className="pg-container">
      <div className="pg-card card">
        <div className="pg-icon">&#128274;</div>
        <h3 className="pg-title">{title}</h3>
        <p className="pg-description">{description}</p>
        <div className="pg-actions">
          <button className="btn btn-primary pg-subscribeBtn" onClick={goToPlans}>
            {cta}
          </button>
          <p className="pg-priceNote">Plans from $9.99/mo · 7-day free trial · Cancel anytime</p>
        </div>
      </div>
    </div>
  );
}

export default PaidGate;
